import React, { useState } from 'react';
import Button from '@components/Button';
import { Axios, getErrorMessage } from '@helpers/ApiService';
import { errorMessage, formRegex, responseTexts } from '@helpers/Constants';
import { useNavigate } from 'react-router-dom';
import { RegisterForm } from '@helpers/Models';
import { useToast } from '@contexts/Toast';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { Logo } from '@components/Logo';
import { TermsAndConditionsModal } from '@components/Modal/TermsAndConditionsModal';

export const Registration: React.FC = () => {
  const navigate = useNavigate();
  const { showToastMessage } = useToast();

  const [formData, setFormData] = useState<RegisterForm>({
    companyName: '',
    contactName: '',
    countryCode: '',
    contactPhone: '',
    contactEmail: '',
    password: '',
    confirmPassword: '',
  });

  const [countryCode, setCountryCode] = useState('+1');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  /**
   * Validates a form field based on its name and value.
   *
   * @param name - The name of the form field to validate.
   * @param value - The value of the form field to validate.
   * @returns An error message if the validation fails, otherwise an empty string.
   *
   * Validates the following fields:
   * - companyName: Must not be empty.
   * - contactName: Must not be empty.
   * - contactPhone: Must be a 10-digit number.
   * - contactEmail: Must be a valid email address.
   * - password: Must be at least 8 characters long, contain at least one uppercase letter, one lowercase letter, one digit, and one special character.
   * - confirmPassword: Must match the password field.
   */
  const validateField = (name: string, value: string): string => {
    const rules: { [key: string]: (value: string) => string } = {
      companyName: (val) => {
        if (!val.trim()) return errorMessage.FIELD_REQUIRED;
        if (!formRegex.EMOJI.test(val)) return errorMessage.INVALID_CHARACTERS;
        if (!formRegex.HTMLTags.test(val))
          return errorMessage.INVALID_HTML_TAGS;
        return '';
      },
      contactName: (val) => {
        if (!val.trim()) return errorMessage.FIELD_REQUIRED;
        if (!formRegex.EMOJI.test(val)) return errorMessage.INVALID_CHARACTERS;
        return '';
      },
      contactPhone: (val) =>
        formRegex.MOBILE_NUMBER.test(val)
          ? ''
          : errorMessage.INVALID_CONTACT_NUMBER,
      contactEmail: (val) =>
        formRegex.EMAIL.test(val) ? '' : errorMessage.INVALID_EMAIL,
      password: (val) =>
        formRegex.PASSWORD.test(val) ? '' : errorMessage.INVALID_PASSWORD,
      confirmPassword: (val) => {
        if (!val) {
          return errorMessage.FIELD_REQUIRED;
        }
        if (val !== formData.password) {
          return errorMessage.PASSWORD_MATCH_FAILED;
        }
        return '';
      },
    };

    return rules[name] ? rules[name](value) : '';
  };

  /**
   * Handles input change events for form fields.
   *
   * @param event - The change event triggered by input or select elements.
   *
   * This function updates the form data state based on the input field's name and value.
   * It also handles special cases such as updating the terms acceptance state and the country code state.
   * Additionally, it validates the field and updates the error state accordingly.
   */
  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const target = event.target;

    if (target.name === 'terms') {
      setTermsAccepted((target as HTMLInputElement).checked);
    } else {
      const { name, value } = target;
      // 🔽 Convert email to lowercase to prevent capital letters
      const processedValue = name === 'contactEmail' ? value.toLowerCase() : value;
      setFormData((prev) => ({ ...prev, [name]: processedValue }));
      // Update the countryCode state when dropdown value changes
      if (target instanceof HTMLSelectElement) {
        setCountryCode(value);
      }
      const error = validateField(name, processedValue);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  /**
   * Validates the form data and updates the error state.
   *
   * This function iterates over the form data, validates each field, and updates the `newErrors` object with the validation results.
   * It also validates whether the terms and conditions are accepted.
   * Finally, it updates the `errors` state with the new errors and returns whether the form is valid.
   *
   * @returns {boolean} - Returns `true` if the form is valid, otherwise `false`.
   */
  const handleValidation = () => {
    const newErrors: { [key: string]: string } = {};

    Object.keys(formData).forEach((key) => {
      const value = formData[key as keyof typeof formData];
      newErrors[key] = validateField(key, String(value));
    });

    newErrors['terms'] = validateTermsAccepted();
    setErrors(newErrors);
    return isFormValid(newErrors);
  };

  /**
   * Validates if the terms and conditions have been accepted.
   *
   * @returns {string} An empty string if terms are accepted, otherwise an error message indicating failure to accept terms and conditions.
   */
  const validateTermsAccepted = () => {
    return termsAccepted ? '' : errorMessage.TERMS_AND_CONDITION_FAILURE;
  };

  /**
   * Checks if the form is valid by ensuring there are no errors.
   *
   * @param errors - An object containing form field errors, where the key is the field name and the value is the error message.
   * @returns `true` if all fields are valid (i.e., no error messages), otherwise `false`.
   */
  const isFormValid = (errors: { [key: string]: string }) => {
    return Object.values(errors).every((error) => !error);
  };

  const handleTermsLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsTermsModalOpen(true);
  };

  const handleTermsAccept = () => {
    setTermsAccepted(true);
    setIsTermsModalOpen(false);
    // Clear terms error if it exists
    setErrors((prev) => ({ ...prev, terms: '' }));
  };

  const handleTermsDecline = () => {
    setTermsAccepted(false);
    setIsTermsModalOpen(false);
  };

  const handleTermsModalClose = () => {
    setIsTermsModalOpen(false);
  };

  const handleRegistration: (event: React.FormEvent) => Promise<void> = async (
    event,
  ) => {
    event.preventDefault();

    if (handleValidation()) {
      const payload = { ...formData, countryCode: countryCode };
      try {
        await Axios.post(ApiEndpoints.REGISTER, payload);
        showToastMessage(responseTexts.REGISTER_SUCCESSFUL, 'success');
        navigate(FrontendRoutes.LOGIN);
      } catch (error) {
        showToastMessage(getErrorMessage(error), 'error');
      }
    } else {
      showToastMessage(errorMessage.INVALID_FORM, 'error');
    }
  };

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <Logo />
        <h2 className="mt-6 text-center text-2xl font-bold tracking-tight text-gray-900">
          Sign up to your Account
        </h2>
        <span className="text-sm flex items-center justify-center whitespace-nowrap">
          Enter your details to create your account and use the platform.
        </span>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
        <form onSubmit={handleRegistration} className="space-y-3">
          <div>
            <label
              htmlFor="companyName"
              className="block text-sm font-medium text-gray-900"
            >
              Business Name *
            </label>
            <input
              id="companyName"
              name="companyName"
              type="text"
              onChange={handleInputChange}
              value={formData.companyName}
              className={`block w-full rounded-md mt-[3px] px-3 py-1.5 text-base text-gray-900 outline outline-1 placeholder:text-gray-400 focus:outline-2 sm:text-sm ${errors.companyName
                  ? 'outline-red-500 border-red-500  focus:outline-red-500'
                  : 'outline-gray-300 focus:outline-primary'
                }`}
            />
            {errors.companyName && (
              <p className="text-red-600 text-sm">{errors.companyName}</p>
            )}
          </div>
          <div>
            <label
              htmlFor="contactName"
              className="block text-sm font-medium text-gray-900"
            >
              Contact Name *
            </label>
            <input
              id="contactName"
              name="contactName"
              type="text"
              onChange={handleInputChange}
              value={formData.contactName}
              className={`block w-full rounded-md mt-[3px] px-3 py-1.5 text-base text-gray-900 outline outline-1 placeholder:text-gray-400 focus:outline-2 sm:text-sm ${errors.contactName
                  ? 'outline-red-500 border-red-500  focus:outline-red-500'
                  : 'outline-gray-300 focus:outline-primary'
                }`}
            />
            {errors.contactName && (
              <p className="text-red-600 text-sm">{errors.contactName}</p>
            )}
          </div>
          <div>
            <label
              htmlFor="contactPhone"
              className="block text-sm font-medium text-gray-900"
            >
              Phone Number *
            </label>
            <div className="flex items-center space-x-2">
              <select
                data-testid="countryCode"
                name="countryCode"
                value={countryCode}
                onChange={handleInputChange}
                className="block rounded-md mt-[3px] px-3 py-2 bg-white text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm h-[38px]"
              >
                <option value="+1">+1</option>
              </select>
              <input
                id="contactPhone"
                name="contactPhone"
                type="text"
                onChange={handleInputChange}
                value={formData.contactPhone}
                className={`block w-full rounded-md mt-[3px] px-3 py-2 text-base text-gray-900 outline outline-1 placeholder:text-gray-400 focus:outline-2 sm:text-sm h-[38px] ${errors.contactPhone ? 'outline-red-500 border-red-500 focus:outline-red-500' : 'outline-gray-300 focus:outline-primary'}`}
              />
            </div>
            {errors.contactPhone && (
              <p className="text-red-600 text-sm">{errors.contactPhone}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="contactEmail"
              className="block text-sm font-medium text-gray-900"
            >
              E - Mail *
            </label>
            <input
              id="contactEmail"
              name="contactEmail"
              type="email"
              onChange={handleInputChange}
              value={formData.contactEmail}
              className={`block w-full rounded-md mt-[3px] px-3 py-1.5 text-base text-gray-900 outline outline-1 placeholder:text-gray-400 focus:outline-2 sm:text-sm ${errors.contactEmail
                  ? 'outline-red-500 border-red-500  focus:outline-red-500'
                  : 'outline-gray-300 focus:outline-primary'
                }`}
            />
            {errors.contactEmail && (
              <p className="text-red-600 text-sm">{errors.contactEmail}</p>
            )}
          </div>
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-900"
            >
              New Password *
            </label>
            <input
              id="password"
              name="password"
              type="password"
              onChange={handleInputChange}
              value={formData.password}
              className={`block w-full rounded-md mt-[3px] px-3 py-1.5 text-base text-gray-900 outline outline-1 placeholder:text-gray-400 focus:outline-2 sm:text-sm ${errors.password
                  ? 'outline-red-500 border-red-500  focus:outline-red-500'
                  : 'outline-gray-300 focus:outline-primary'
                }`}
            />
            {errors.password && (
              <p className="text-red-600 text-sm">{errors.password}</p>
            )}
          </div>
          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-gray-900"
            >
              Confirm Password *
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              onChange={handleInputChange}
              value={formData.confirmPassword}
              className={`block w-full rounded-md mt-[3px] px-3 py-1.5 text-base text-gray-900 outline outline-1 placeholder:text-gray-400 focus:outline-2 sm:text-sm ${errors.confirmPassword
                  ? 'outline-red-500 border-red-500  focus:outline-red-500'
                  : 'outline-gray-300 focus:outline-primary'
                }`}
            />
            {errors.confirmPassword && (
              <p className="text-red-600 text-sm">{errors.confirmPassword}</p>
            )}
          </div>
          <div className="mb-3 flex items-center">
            <input
              data-testid="termscondition"
              id="terms"
              name="terms"
              type="checkbox"
              className="h-4 w-4 shrink-0 accent-primary focus:ring-primary border-gray-300 rounded"
              checked={termsAccepted}
              onChange={handleInputChange}
            />
            <label htmlFor="terms" className="text-gray-800 ml-3 block text-sm">
              I accept{' '}
              <button
                type="button"
                onClick={handleTermsLinkClick}
                className="text-primary font-semibold hover:underline ml-1 underline"
              >
                WGB&apos;s Terms and Conditions
              </button>
            </label>
          </div>
          {errors.terms && (
            <p className="text-red-600 text-sm">{errors.terms}</p>
          )}
          <div>
            <Button className="mt-6" name={'Sign up'}></Button>
            <p className="text-gray-800 text-sm mt-3 text-center">
              Already have an account?{' '}
              <a
                href="/login"
                className="text-primary font-semibold hover:underline ml-1"
              >
                Log in
              </a>
            </p>
          </div>
        </form>
      </div>

      <TermsAndConditionsModal
        isOpen={isTermsModalOpen}
        onClose={handleTermsModalClose}
        onAccept={handleTermsAccept}
        onDecline={handleTermsDecline}
      />
    </div>
  );
};